/**
 * A disclosure that shows partial contents and, if the contents are taller
 * than some max height, an optional fade and button that toggles whether the
 * full contents are shown or clipped.
 * */
import React, { useRef, useState, useCallback } from 'react';
import classnames from 'classnames';
import { useResizeObserver } from '@wix/da-hooks/pkg/useResizeObserver';
import { useThrottle } from '@wix/da-hooks/pkg/useThrottle';
import s from './CollapsibleBlock.scss';
var RESIZE_THROTTLE_DELAY = 300;
var RESIZE_THROTTLE_OPTIONS = { trailing: true };
var CollapsibleBlock = function (props) {
    var buttonTextCollapsed = props.buttonTextCollapsed, buttonTextExpanded = props.buttonTextExpanded, maxHeight = props.maxHeight, className = props.className, collapsedClassName = props.collapsedClassName, shutterBlockClassName = props.shutterBlockClassName, buttonClassName = props.buttonClassName, showFade = props.showFade, fadeClassName = props.fadeClassName, renderToggleButton = props.renderToggleButton, onCollapsed = props.onCollapsed, onExpanded = props.onExpanded, children = props.children;
    var contentBlockRef = useRef(null);
    var _a = useState(true), collapsed = _a[0], setCollapsed = _a[1];
    // will default to taller in SSR
    var _b = useState(maxHeight), contentHeight = _b[0], setContentHeight = _b[1];
    var isTallerThanMaxHeight = contentHeight > maxHeight;
    // we only show 'as collapsed' if there's enough content to spill over
    var showAsCollapsed = isTallerThanMaxHeight && collapsed;
    // re-calculate if the size of the contents changes
    // note that ResizeObserver is more of a proxy for DOMSubtreeModified,
    // so it's possible we receive resize events to the same size
    var handleResize = useThrottle(function () {
        var newHeight = contentBlockRef.current.clientHeight;
        if (newHeight !== contentHeight) {
            setContentHeight(newHeight);
            setCollapsed(newHeight > maxHeight);
        }
    }, [maxHeight, contentHeight], RESIZE_THROTTLE_DELAY, RESIZE_THROTTLE_OPTIONS);
    useResizeObserver(contentBlockRef, handleResize);
    var toggleButtonClick = useCallback(function () {
        setCollapsed(!collapsed);
        if (!collapsed) {
            onCollapsed && onCollapsed();
        }
        else {
            onExpanded && onExpanded();
        }
    }, [collapsed, onCollapsed, onExpanded]);
    var classes = classnames(showAsCollapsed && collapsedClassName, className);
    return (React.createElement("div", { className: classes },
        React.createElement("div", { className: s['fade-container'] },
            React.createElement("div", { className: classnames(s['shutter-block'], shutterBlockClassName), style: {
                    maxHeight: collapsed ? maxHeight : contentHeight,
                } },
                React.createElement("div", { ref: contentBlockRef }, children)),
            showAsCollapsed && showFade && (React.createElement("div", { className: classnames(s['fade'], fadeClassName) }))),
        isTallerThanMaxHeight &&
            (renderToggleButton ? (renderToggleButton({ collapsed: collapsed, onClick: toggleButtonClick })) : (React.createElement("button", { className: classnames(buttonClassName, s['toggle-button']), onClick: toggleButtonClick }, collapsed ? buttonTextCollapsed : buttonTextExpanded)))));
};
CollapsibleBlock.displayName = 'CollapsibleBlock';
export default CollapsibleBlock;
//# sourceMappingURL=CollapsibleBlock.js.map